import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/RCS-Logo-White.png";
import CollapsedLogo from "../assets/RCS-Logo-White.png";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/auth-slice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import clsx from "clsx";
import TerumoWatermark from "../assets/terumo-logo1.webp";
import UrcWatermark from "../assets/urc-profile.jpg";
import LinksWatermark from "../assets/link_1x1.png";

const Sidebar = ({ forceExpanded = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [textVisible, setTextVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const user = useSelector((state) => state.auth.user);
  const { first_name = '', last_name = '', department = '' } = user || {};

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;
      const isLarge = Boolean(forceExpanded || width >= 1025);
      setIsLargeScreen(isLarge);
      setTextVisible(isLarge);
    };

    window.addEventListener("resize", handleResize);

    // Run it once when the component mounts
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [forceExpanded]);

  const isActive = (path) =>
    location.pathname === path;

  const handleLogout = () => {
    dispatch(logoutUser()).then((data) => {
      const message = data?.payload?.message;
      if (data?.payload?.success) {
        toast.success(message, { position: "top-right" });
      } else {
        toast.error(message, { position: "top-right" });
      }
      sessionStorage.removeItem("passwordResetNotificationClosed");
    });
  };

  const handleLogoutConfirmation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-sign-out-alt text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">Confirm Logout</h2>
              <p className="text-grey">Are you sure you want to sign out of your account?</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };


  return (
    <div className="flex font-poppins h-full">


      {/* Modern Sidebar */}
      <div
        className={clsx(
          "h-full bg-blue text-white transition-all duration-500 ease-out backdrop-blur-xl shadow-2xl border-r border-white/10",
          isLargeScreen ? "w-72" : "w-20"
        )}
        style={{ willChange: 'transform', transform: 'translateZ(0)' }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-4 w-32 h-32 bg-yellow/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-32 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        {/* Logo Section */}
        <div
          className={`relative flex items-center justify-center p-6 border-b border-white/10 backdrop-blur-sm ${
            isLargeScreen ? "h-28" : "h-20"
          }`}
        >
          <div className="relative">
            <img
              src={isLargeScreen ? Logo : CollapsedLogo}
              alt="Logo"
              className={`transition-all duration-500 ${
                isLargeScreen
                  ? "w-44 h-16 object-contain"
                  : "w-12 h-12 object-contain"
              }`}
            />
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="mt-8 px-4 space-y-3 overflow-y-auto pb-32" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
          <Link
            to="/corporate/corporate-dashboard"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/corporate/corporate-dashboard")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
          >
            {/* Icon Container */}
            <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
              isActive("/corporate/corporate-dashboard") ? "text-blue" : "text-white hover:text-yellow"
            }`}>
              <i className="fa-solid fa-chart-line text-lg"></i>
              {/* Icon glow effect */}
              {isActive("/corporate/corporate-dashboard") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {/* Text Label */}
            {textVisible && (
              <span className={`font-semibold transition-all duration-300 ${
                isActive("/corporate/corporate-dashboard") 
                  ? "text-blue" 
                  : "text-white group-hover:text-yellow"
              }`}>
                Dashboard
              </span>
            )}

            {/* Hover effect background */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          <Link
            to="/corporate/corporate-shipment-management"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/corporate/corporate-shipment-management")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
            style={{ animationDelay: "100ms" }}
          >
            {/* Icon Container */}
            <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
              isActive("/corporate/corporate-shipment-management") ? "text-blue" : "text-white group-hover:text-yellow"
            }`}>
              <i className="fa-solid fa-dolly text-lg"></i>
              {/* Icon glow effect */}
              {isActive("/corporate/corporate-shipment-management") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {/* Text Label */}
            {textVisible && (
              <span className={`font-semibold transition-all duration-300 ${
                isActive("/corporate/corporate-shipment-management") 
                  ? "text-blue" 
                  : "text-white group-hover:text-yellow"
              }`}>
                Shipment Management
              </span>
            )}

            {/* Hover effect background */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          <Link
            to="/corporate/corporate-analytics"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/corporate/corporate-analytics")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
            style={{ animationDelay: "200ms" }}
          >
            {/* Icon Container */}
            <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
              isActive("/corporate/corporate-analytics") ? "text-blue" : "text-white group-hover:text-yellow"
            }`}>
              <i className="fa-solid fa-chart-pie text-lg"></i>
              {/* Icon glow effect */}
              {isActive("/corporate/corporate-analytics") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {/* Text Label */}
            {textVisible && (
              <span className={`font-semibold transition-all duration-300 ${
                isActive("/corporate/corporate-analytics")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              }`}>
                Analytics
              </span>
            )}

            {/* Hover effect background */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
        </nav>

        {/* Enhanced Profile Section */}
        <div className="absolute bottom-0 left-0 w-full border-t border-white/10 backdrop-blur-sm">
          <div className="p-6 space-y-4">
            {/* Profile Info */}
            <div className={`flex items-center gap-4 ${isLargeScreen ? "justify-start" : "justify-center"}`}>
              {/* Enhanced Avatar with Department Logic */}
              <div className="relative">
                {(department === "URC" || department === "Terumo" || department === "Links") ? (
                  <div className="bg-white rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg p-2">
                    <img
                      src={
                        department === "URC"
                          ? UrcWatermark
                          : department === "Links"
                          ? LinksWatermark
                          : TerumoWatermark
                      }
                      alt={`${department} Logo`}
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                ) : (
                  <div className="bg-yellow text-blue font-bold rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg">
                    {`${first_name?.[0] || ""}${last_name?.[0] || ""}`}
                  </div>
                )}
                
                {/* Online indicator */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm">
                  <div className="w-full h-full bg-green-500 rounded-full animate-ping opacity-75"></div>
                </div>
              </div>

              {/* Profile Details */}
              {textVisible && (
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate">
                    {`${first_name || ""} ${last_name || ""}`}
                  </p>
                  <p className="text-xs text-white/70 truncate">{department || ""}</p>
                </div>
              )}
            </div>

            <button
              className={clsx(
                "group relative flex items-center gap-3 w-full px-4 py-3",
                "bg-red hover:bg-red/90",
                "text-white rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl",
                isLargeScreen ? "justify-start" : "justify-center"
              )}
              onClick={handleLogoutConfirmation}
            >
              {/* Content above the overlay */}
              <div className="relative z-10 flex items-center gap-3">
                <div className="flex items-center justify-center w-5 h-5">
                  <i className="fa-solid fa-right-from-bracket text-sm group-hover:text-yellow transition-colors duration-300"></i>
                </div>

                {textVisible && (
                  <span className="font-semibold text-sm group-hover:text-yellow transition-colors duration-300">
                    Logout
                  </span>
                )}
              </div>

              {/* Glow overlay lives under the text and ignores pointer events */}
              <div className="pointer-events-none absolute inset-0 z-0 rounded-2xl bg-red/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
